/**
 * AppSettingsContext
 *
 * Loads the application's key/value settings once at app startup and
 * exposes them to the rest of the component tree via useAppSettings().
 *
 * The provider is mounted inside AuthProvider so that the underlying
 * /appSettings API call is authenticated. Until settings finish loading,
 * consumers see an empty map and the `isLoading` flag set to true —
 * callers should render sensible fallbacks in that case (e.g., hide the
 * Help menu, use baked-in defaults).
 *
 * The settings are also available as fully-typed helper getters for the
 * well-known keys used by the Support Contact widget and the Help page.
 * Unknown keys can be read with `get(key, default)`.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { appSettingsApi, type AppSetting } from '../api/appSettings';
import { useAuth } from './AuthContext';

// Known keys — keep in sync with the seed migration.
export const APP_SETTING_KEYS = {
  supportTicketUrlBase: 'support.ticketUrlBase',
  supportSubjectPrefix: 'support.subjectPrefix',
  supportSubjectTemplate: 'support.subjectTemplate',
  supportDescriptionTemplate: 'support.descriptionTemplate',
  helpUserGuideUrl: 'help.userGuideUrl',
  helpDemoVideoUrl: 'help.demoVideoUrl',
  helpPageEnabled: 'help.pageEnabled',
  calendarTabEnabled: 'calendar.tabEnabled',
  calendarEditingEnabled: 'calendar.editingEnabled',
  calendarPageEnabled: 'calendar.pageEnabled',
} as const;

export type AppSettingKey = (typeof APP_SETTING_KEYS)[keyof typeof APP_SETTING_KEYS];

interface AppSettingsContextType {
  settings: AppSetting[];
  settingsMap: Record<string, string | null>;
  isLoading: boolean;
  error: string | null;
  /** Get a single setting value, falling back to the provided default if missing. */
  get: (key: string, fallback?: string) => string;
  /** Read a setting as a boolean. Anything other than 'true' (case-insensitive) is false. */
  getBool: (key: string, fallback?: boolean) => boolean;
  /** Refresh the cache from the server. Call after a superAdmin edits a value. */
  refresh: () => Promise<void>;
}

const AppSettingsContext = createContext<AppSettingsContextType | undefined>(undefined);

export const AppSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [settings, setSettings] = useState<AppSetting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const rows = await appSettingsApi.list();
      setSettings(rows);
    } catch (err) {
      console.error('[AppSettings] Failed to load settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to load app settings');
      setSettings([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load once we know the user is authenticated. If the user is not
  // authenticated (e.g. on /login), we skip the fetch and report "not loading"
  // so nothing blocks on us.
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setSettings([]);
      setIsLoading(false);
      return;
    }
    load();
  }, [authLoading, isAuthenticated, load]);

  const settingsMap = useMemo(() => {
    const map: Record<string, string | null> = {};
    for (const row of settings) {
      map[row.settingKey] = row.settingValue;
    }
    return map;
  }, [settings]);

  const get = useCallback(
    (key: string, fallback: string = '') => {
      const value = settingsMap[key];
      if (value === undefined || value === null || value === '') return fallback;
      return value;
    },
    [settingsMap]
  );

  const getBool = useCallback(
    (key: string, fallback: boolean = false) => {
      const value = settingsMap[key];
      if (value === undefined || value === null || value === '') return fallback;
      return value.trim().toLowerCase() === 'true';
    },
    [settingsMap]
  );

  const value = useMemo<AppSettingsContextType>(
    () => ({
      settings,
      settingsMap,
      isLoading,
      error,
      get,
      getBool,
      refresh: load,
    }),
    [settings, settingsMap, isLoading, error, get, getBool, load]
  );

  return <AppSettingsContext.Provider value={value}>{children}</AppSettingsContext.Provider>;
};

export const useAppSettings = (): AppSettingsContextType => {
  const ctx = useContext(AppSettingsContext);
  if (!ctx) {
    throw new Error('useAppSettings must be used within an AppSettingsProvider');
  }
  return ctx;
};

/**
 * Build the Request Support URL by interpolating templates from app settings
 * against the current user. Used by both the UserMenu widget and the HelpPage.
 *
 * Tokens: {prefix}, {userName}, {userEmail}, {userRole}, {orgName}, {pageUrl}
 */
export function buildSupportTicketUrl(params: {
  urlBase: string;
  subjectTemplate: string;
  descriptionTemplate: string;
  subjectPrefix: string;
  userName: string;
  userEmail: string;
  userRole: string;
  orgName: string;
  pageUrl: string;
}): string {
  const tokens: Record<string, string> = {
    '{prefix}': params.subjectPrefix,
    '{userName}': params.userName,
    '{userEmail}': params.userEmail,
    '{userRole}': params.userRole,
    '{orgName}': params.orgName,
    '{pageUrl}': params.pageUrl,
  };

  const interpolate = (template: string) =>
    Object.entries(tokens).reduce(
      (acc, [token, value]) => acc.split(token).join(value),
      template
    );

  const subject = interpolate(params.subjectTemplate);
  const description = interpolate(params.descriptionTemplate);

  const url = new URL(params.urlBase);
  url.searchParams.set('subject', subject);
  url.searchParams.set('description', description);
  return url.toString();
}
